import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Modal,
  TextField,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import { Edit, AttachMoney } from '@mui/icons-material';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';

function ModelFundsCard({ organizationId, organizationName }) {
  const { showSuccess, showError } = useToast();
  const [funds, setFunds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    funds: ''
  });
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchFunds();
  }, [organizationId]);

  const fetchFunds = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchOrganizationFunds(organizationId);
      
      if (result.success) {
        setFunds(result.data.funds);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load organization funds');
    }
    
    setLoading(false);
  };

  const handleEditClick = () => {
    setEditForm({
      funds: funds !== null ? funds.toString() : ''
    });
    setEditModalOpen(true);
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setEditForm({ funds: '' });
  };

  const handleFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    // Validate input
    const fundsValue = parseFloat(editForm.funds);
    
    if (isNaN(fundsValue) || fundsValue < 0) {
      showError('Please enter a valid positive number for funds', 'Validation Error');
      return;
    }

    setEditLoading(true);

    try {
      const result = await apiService.updateOrganizationFunds(organizationId, fundsValue);
      
      if (result.success) {
        console.log('Organization funds updated successfully:', result.data);
        setFunds(fundsValue);
        setEditModalOpen(false);
        setEditForm({ funds: '' });
        showSuccess('Organization funds updated successfully!', 'Success');
      } else {
        console.error('Failed to update organization funds:', result.error);
        showError(`Failed to update organization funds: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error updating organization funds:', error);
      showError('Failed to update organization funds. Please try again.', 'Error');
    } finally {
      setEditLoading(false);
    }
  };

  const formatFunds = (fundsValue) => {
    if (fundsValue === null || fundsValue === undefined) return 'No funds set';
    return `$${fundsValue.toFixed(2)}`;
  };

  if (loading) {
    return (
      <Card sx={{ bgcolor: 'background.default', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            💰 Model Funds
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Loading funds...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ bgcolor: 'background.default', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            💰 Model Funds
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
          <Button 
            variant="outlined" 
            onClick={fetchFunds}
            sx={{ mt: 2 }}
            size="small"
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ bgcolor: 'background.default', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" fontWeight={600}>
              💰 Model Funds
            </Typography>
            <IconButton 
              size="small" 
              onClick={handleEditClick}
              sx={{ color: 'primary.main' }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Current Funds
            </Typography>
            <Typography 
              variant="h5" 
              fontWeight={700} 
              color="primary.main"
              sx={{ 
                letterSpacing: 0.5
              }}
            >
              {formatFunds(funds)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Edit Funds Modal */}
      <Modal
        open={editModalOpen}
        onClose={handleModalClose}
        aria-labelledby="edit-funds-modal"
        aria-describedby="edit-funds-form"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h6" gutterBottom>
            Edit Organization Funds
          </Typography>
          
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Funds Amount"
              type="number"
              value={editForm.funds}
              onChange={(e) => handleFormChange('funds', e.target.value)}
              disabled={editLoading}
              placeholder="Enter funds amount"
              InputProps={{
                startAdornment: <AttachMoney sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              helperText="Enter the amount of funds available for this organization"
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleModalClose}
                disabled={editLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSubmit}
                disabled={editLoading || !editForm.funds.trim()}
                sx={{ minWidth: 80 }}
              >
                {editLoading ? 'Updating...' : 'Update'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default ModelFundsCard;
